import { i18n } from '../i18n/index.ts';
export interface RequestOptions {
  idempotencyKey?: string;
  sensitive?: boolean;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}
export class ApiError extends Error {
  status: number;
  code: string;
  traceId?: string;
  details?: unknown;
  fields?: Array<{field:string;item_id?:string;code:string}>;
  constructor(message: string, status: number, code = 'REQUEST_FAILED', traceId?: string, details?: unknown, fields?: Array<{field:string;item_id?:string;code:string}>) {
    super(message + (traceId ? ' · ' + traceId : ''));
    this.name = 'ApiError'; this.status = status; this.code = code; this.traceId = traceId; this.details = details; this.fields = fields;
  }
}
export type Request = <T = unknown>(path: string, body?: unknown, method?: string, options?: RequestOptions) => Promise<T>;
export type StreamRequest = (path: string, body: unknown, options?: RequestOptions) => Promise<Response>;

// 静态托管与 API 分开部署时，构建期用 VITE_API_BASE_URL 指向云托管地址；同源部署留空。
const API_BASE = String(import.meta.env?.VITE_API_BASE_URL || '').replace(/\/+$/, '');
const apiOrigin = () => (API_BASE ? new URL(API_BASE).origin : window.location.origin);

export function retryIdentity(method: string, path: string, body: unknown, options: RequestOptions = {}) {
  const verb = method.toUpperCase();
  if (options.sensitive) {
    if (!options.idempotencyKey) throw new Error('Sensitive mutations require an explicit idempotency key');
    return verb + ' ' + path + ' sensitive:' + options.idempotencyKey;
  }
  return verb + ' ' + path + ' ' + (body === undefined ? '' : body instanceof FormData
    ? JSON.stringify([...(body as FormData).entries()].map(([k, v]) => [k, typeof v === 'string' ? v : [v.name, v.size, v.lastModified]]))
    : JSON.stringify(body));
}

export async function readNdjson(response: Response, onFrame: (frame: any) => void | Promise<void>, signal?: AbortSignal) {
  if (!response.body) throw new ApiError(i18n.global.t('common.requestFailed'), 0, 'STREAM_PROTOCOL_ERROR');
  const reader = response.body.getReader(), decoder = new TextDecoder();
  let buffer = '';
  const consume = async (line: string) => {
    const value = line.trim();
    if (!value) return;
    if (value.length > 65536) throw new ApiError(i18n.global.t('common.requestFailed'), 0, 'STREAM_PROTOCOL_ERROR');
    let frame: any;
    try { frame = JSON.parse(value); } catch { throw new ApiError(i18n.global.t('common.requestFailed'), 0, 'STREAM_PROTOCOL_ERROR'); }
    await onFrame(frame);
  };
  const abort = () => { void reader.cancel().catch(() => {}); };
  signal?.addEventListener('abort', abort, { once: true });
  try {
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) break;
      buffer += decoder.decode(chunk.value, { stream: true });
      let boundary = buffer.indexOf('\n');
      while (boundary >= 0) {
        await consume(buffer.slice(0, boundary)); buffer = buffer.slice(boundary + 1);
        boundary = buffer.indexOf('\n');
      }
      if (buffer.length > 65536) throw new ApiError(i18n.global.t('common.requestFailed'), 0, 'STREAM_PROTOCOL_ERROR');
    }
    buffer += decoder.decode(); await consume(buffer);
  } catch (cause) {
    if (cause instanceof ApiError || cause instanceof Error && cause.name === 'AbortError') throw cause;
    if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
    throw new ApiError(i18n.global.t('common.requestFailed'), 0, 'STREAM_PROTOCOL_ERROR');
  } finally {
    signal?.removeEventListener('abort', abort); reader.releaseLock();
  }
}

export function createApiClient(getToken: () => string | null, expired: () => void = () => {}) {
  // Retain the key when the outcome is unknown, so a manual retry cannot duplicate a write.
  const pendingKeys = new Map<string, string>();
  const request: Request = async <T>(path: string, body?: unknown, method?: string, options: RequestOptions = {}): Promise<T> => {
    if (!path.startsWith('/') || path.startsWith('//')) throw new Error('Invalid API path');
    const verb = method || (body === undefined ? 'GET' : 'POST');
    const multipart = body instanceof FormData;
    const identity = !['GET', 'HEAD'].includes(verb) ? retryIdentity(verb, path, body, options) : '';
    const serialized = body === undefined ? undefined : multipart ? body as FormData : JSON.stringify(body);
    const fingerprint = options.sensitive ? undefined : identity;
    const headers = new Headers(options.headers);
    const token = getToken();
    if (token) headers.set('authorization', 'Bearer ' + token);
    if (serialized !== undefined && !multipart) headers.set('content-type', 'application/json');
    if (!['GET', 'HEAD'].includes(verb)) {
      const key = options.idempotencyKey || (fingerprint ? pendingKeys.get(fingerprint) : undefined) || crypto.randomUUID();
      if (fingerprint) pendingKeys.set(fingerprint, key);
      headers.set('Idempotency-Key', key);
    }
    let response: Response;
    try { response = await fetch(API_BASE + '/api/v1' + path, { method: verb, headers, body: serialized, signal: options.signal }); }
    catch (cause) {
      if (cause instanceof Error && cause.name === 'AbortError') throw cause;
      throw new ApiError(i18n.global.t('common.networkError'), 0, 'NETWORK_ERROR');
    }
    const result = await response.json().catch(() => null);
    if (fingerprint && response.status < 500 && response.status !== 429) pendingKeys.delete(fingerprint);
    if (!response.ok || !result?.success) {
      if (response.status === 401 && token && !path.startsWith('/auth/')) expired();
      throw new ApiError(result?.error?.message || i18n.global.t('common.requestFailed'), response.status, result?.error?.code, result?.trace_id || response.headers.get('x-trace-id') || undefined, result?.error?.details, result?.error?.fields);
    }
    return result.data as T;
  };
  const stream: StreamRequest = async (path, body, options = {}) => {
    if (!path.startsWith('/') || path.startsWith('//')) throw new Error('Invalid API path');
    const headers = new Headers(options.headers), token = getToken();
    headers.set('content-type', 'application/json'); headers.set('accept', 'application/x-ndjson');
    if (token) headers.set('authorization', 'Bearer ' + token);
    let response: Response;
    try { response = await fetch(API_BASE + '/api/v1' + path, { method: 'POST', headers, body: JSON.stringify(body), signal: options.signal }); }
    catch (cause) {
      if (cause instanceof Error && cause.name === 'AbortError') throw cause;
      throw new ApiError(i18n.global.t('common.networkError'), 0, 'NETWORK_ERROR');
    }
    if (!response.ok) {
      const result = await response.json().catch(() => null);
      if (response.status === 401 && token && !path.startsWith('/auth/')) expired();
      throw new ApiError(result?.error?.message || i18n.global.t('common.requestFailed'), response.status, result?.error?.code, result?.trace_id || response.headers.get('x-trace-id') || undefined, result?.error?.details, result?.error?.fields);
    }
    if (!response.headers.get('content-type')?.toLowerCase().startsWith('application/x-ndjson')) {
      void response.body?.cancel().catch(() => {});
      throw new ApiError(i18n.global.t('common.requestFailed'), 0, 'STREAM_PROTOCOL_ERROR');
    }
    return response;
  };
  async function download(url: string, filename: string) {
    const target = new URL(url, apiOrigin());
    if (target.origin !== apiOrigin() || !target.pathname.startsWith('/api/v1/')) throw new Error('Invalid download URL');
    const token = getToken();
    const response = await fetch(target, { headers: token ? { authorization: 'Bearer ' + token } : {} });
    if (!response.ok) {
      if (response.status === 401) expired();
      const result = await response.json().catch(() => null);
      throw new ApiError(result?.error?.message || i18n.global.t('common.downloadExpired'), response.status, result?.error?.code, result?.trace_id);
    }
    saveBlob(await response.blob(), filename);
  }
  return { request, stream, download };
}
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = filename;
  document.body.append(anchor); anchor.click(); anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
